import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import ConfirmPopup from '@components/ConfirmPopup'
import CustomPopover, { CustomPopoverProps } from '@components/CustomPopover'
import { CustomColumnType, MAX_CUSTOM_CATEGORY_LENGTH, ShortListCustomField } from '@modules/shortlist/types'
import { generateIdFromString } from '@modules/shortlist/utils'
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { colors } from '@theme/variables'

import EditCustomColumnSection, { EditCustomColumnSectionProps } from '.'
import { PopoverInner } from '../ShortlistProfileButton/index.styles'
import CustomColumnCategoryRow from '../ShortlistTable/CustomColumnPopover/CustomColumnCategoryRow'
import { AddNewCategoryButton, ValidationMessage } from '../ShortlistTable/CustomColumnPopover/index.styles'

const EditCustomColumnSectionPopover = ({
  onSave,
  onCancel,
  customColumn,
  activeShortlist,
  anchor,
}: EditCustomColumnSectionProps & { anchor: CustomPopoverProps['anchor'] }) => {
  const { t } = useTranslation()
  const [customColumnState, setCustomColumnState] = useState<Partial<ShortListCustomField>>(
    customColumn || { fieldOptions: [], fieldName: '' },
  )
  const [showConfirmUnsavedChangesPopup, setShowConfirmUnsavedChangesPopup] = useState(false)
  const [showConfirmDeleteCategoryPopup, setShowConfirmDeleteCategoryPopup] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<number | undefined>(undefined)
  const [categoriesErrors, setCategoriesErrors] = useState<(string | undefined)[]>([])
  const [dataTypeError, setDataTypeError] = useState<string | undefined>(undefined)

  const handleOptionDelete = useCallback(
    (indexToDelete: number) => {
      setCustomColumnState({
        ...customColumnState,
        fieldOptions: customColumnState.fieldOptions?.filter((_, index) => index !== indexToDelete),
      })
      setCategoriesErrors(categoriesErrors.filter((_, index) => index !== indexToDelete))
      if (customColumn?.id) {
        onSave?.(
          {
            ...customColumn,
            id: customColumn.id!,
            fieldName: customColumn.fieldName!,
            fieldType: customColumn.fieldType!,
            fieldOptions: customColumn.fieldOptions?.filter((_, index) => index !== indexToDelete),
          },
          false,
        )
      }
    },
    [customColumnState, categoriesErrors, customColumn, onSave],
  )

  const handleDeleteOptionWithConfirmation = useCallback(
    (indexToDelete: number) => {
      if (!customColumnState.id) {
        handleOptionDelete(indexToDelete)
      } else {
        setCategoryToDelete(indexToDelete)
        setShowConfirmDeleteCategoryPopup(true)
      }
    },
    [customColumnState.id, handleOptionDelete],
  )

  const changesExist = useMemo(
    () =>
      customColumnState.fieldName !== (customColumn?.fieldName || '') ||
      customColumnState.fieldType !== customColumn?.fieldType ||
      customColumnState.fieldOptions?.length !== (customColumn?.fieldOptions || []).length ||
      customColumnState.fieldOptions?.some((option, index) => customColumn?.fieldOptions?.[index].value !== option.value),
    [customColumnState, customColumn],
  )

  const handleClose = useCallback(() => {
    if (changesExist) {
      setShowConfirmUnsavedChangesPopup(true)
    } else {
      onCancel?.()
    }
  }, [changesExist, onCancel])

  const handleOptionOrderChange = useCallback(
    (currentOrderIndex: number, newOrderIndex: number) => {
      let newOptions = [...(customColumnState.fieldOptions ?? [])]
      const categoryToMove = newOptions.splice(currentOrderIndex, 1)[0]
      newOptions = [...newOptions.slice(0, newOrderIndex), categoryToMove, ...newOptions.slice(newOrderIndex)]

      setCustomColumnState({
        ...customColumnState,
        fieldOptions: newOptions,
      })

      let newCategoriesErrors = [...(categoriesErrors ?? [])]

      const errorToMove = categoriesErrors.splice(currentOrderIndex, 1)[0]
      newCategoriesErrors = [
        ...newCategoriesErrors.slice(0, newOrderIndex),
        errorToMove,
        ...newCategoriesErrors.slice(newOrderIndex),
      ]

      setCategoriesErrors(newCategoriesErrors)
    },
    [customColumnState, categoriesErrors],
  )

  const handleOptionUpdate = useCallback(
    (indexToUpdate: number, newValue: string) => {
      if (newValue.length > MAX_CUSTOM_CATEGORY_LENGTH) return

      setCustomColumnState({
        ...customColumnState,
        fieldOptions: customColumnState.fieldOptions?.map((i, idx) => (idx === indexToUpdate ? { ...i, value: newValue } : i)),
      })
    },
    [customColumnState],
  )

  const hasCategoryOptions = customColumnState.fieldType === CustomColumnType.AUTOCOMPLETE
  const setColumnName = useCallback(
    (newFieldName: string | undefined) => {
      setCustomColumnState({ ...customColumnState, fieldName: newFieldName as string })
    },
    [customColumnState],
  )

  const handleDataTypeChange = useCallback(
    (fieldType: CustomColumnType) => {
      if (fieldType === CustomColumnType.AUTOCOMPLETE) {
        setCustomColumnState({
          ...customColumnState,
          fieldType,
          fieldOptions: [{ id: generateIdFromString(''), value: '' }],
        })
      } else {
        setCustomColumnState({
          ...customColumnState,
          fieldType,
          fieldOptions: [],
        })
      }
    },
    [customColumnState],
  )

  const handleConfirm = useCallback(
    (hasTitleError: boolean) => {
      /* eslint-disable no-param-reassign */
      const categoryValidationErrors = customColumnState.fieldOptions?.reduce((errors, fieldOption, index) => {
        if (!fieldOption?.value) {
          errors[index] = translationKeys.CUSTOM_LIST_CUSTOM_COLUMN_CATEGORY_NAME_SHOULD_NOT_BE_EMPTY
          return errors
        }

        if (
          customColumnState.fieldOptions?.find(
            (otherFieldOption, otherIndex) => otherFieldOption === fieldOption && index !== otherIndex,
          )
        ) {
          errors[index] = translationKeys.CUSTOM_LIST_CUSTOM_COLUMN_CATEGORY_NAME_SHOULD_NOT_DUPLICATE
          return errors
        }

        errors[index] = undefined
        return errors
      }, [] as (string | undefined)[])
      /* eslint-enable no-param-reassign */

      const categoriesErrorsExist = categoryValidationErrors?.some(index => !!index)
      setCategoriesErrors(categoryValidationErrors ?? [])
      const isFieldTypeAbsent = !customColumnState.fieldType
      const newDataTypeError = isFieldTypeAbsent ? t(translationKeys.CUSTOM_LIST_CUSTOM_COLUMN_DATA_TYPE_IS_REQUIRED) : undefined
      setDataTypeError(newDataTypeError)

      if (!customColumnState.fieldName || categoriesErrorsExist || isFieldTypeAbsent || hasTitleError) return

      onSave?.(
        {
          id: customColumnState.id!,
          fieldType: customColumnState.fieldType!,
          fieldOptions: customColumnState.fieldOptions?.map(i => ({
            id: i.id ? i.id : generateIdFromString(i.value),
            value: i.value.trim().replace(/\s\s+/g, ' '),
          })),
          fieldName: customColumnState.fieldName.trim().replace(/\s\s+/g, ' '),
        },
        true,
      )
    },
    [customColumnState, t, onSave],
  )

  return (
    <CustomPopover id="shortlist_custom_column_popover" anchor={anchor} setAnchor={handleClose}>
      <PopoverInner width={270}>
        <EditCustomColumnSection
          onCancel={handleClose}
          onSuccess={handleConfirm}
          onChange={setColumnName}
          submitButtonText={t(translationKeys.CUSTOM_LIST_POPUP_CONFIRM_BTN)}
          activeShortlist={activeShortlist}
          title={t(translationKeys.CUSTOM_LIST_CUSTOM_COLUMN_POPOVER_TITLE)}
          subTitle={
            customColumnState.fieldType === CustomColumnType.AUTOCOMPLETE
              ? t(translationKeys.CUSTOM_LIST_CUSTOM_COLUMN_POPOVER_TEXT_CATEGORY)
              : t(translationKeys.CUSTOM_LIST_CUSTOM_COLUMN_POPOVER_TEXT_DEFAULT)
          }
          customColumn={customColumnState}
        >
          {!customColumnState?.id && (
            <FormControl variant="outlined" style={{ width: '100%' }} margin="normal">
              <InputLabel
                sx={{ background: colors.white, padding: '0 6px', marginTop: '2px', color: '#00000099' }}
                id="profile-alert-select"
                shrink
              >
                {t(translationKeys.DATA_TYPE)}
              </InputLabel>
              <Select
                id="profile-alert-select"
                notched
                value={customColumnState.fieldType}
                labelId="profile-alert-select"
                onChange={(e: SelectChangeEvent<CustomColumnType>) => handleDataTypeChange(e.target.value as CustomColumnType)}
              >
                <MenuItem value={CustomColumnType.AUTOCOMPLETE}>{t(translationKeys.AUTOCOMPLETE)}</MenuItem>
                <MenuItem value={CustomColumnType.FREE_TEXT}>{t(translationKeys.FREE_TEXT)}</MenuItem>
              </Select>
            </FormControl>
          )}
          {dataTypeError && <ValidationMessage>{dataTypeError}</ValidationMessage>}
          {hasCategoryOptions && (
            <>
              {customColumnState.fieldOptions?.map((fieldOption, index) => (
                <Box key={fieldOption.id} mb="10px">
                  <CustomColumnCategoryRow
                    deletable={customColumnState.fieldOptions?.length !== 1}
                    index={index}
                    category={fieldOption}
                    existingCategories={customColumnState.fieldOptions}
                    onDelete={() => handleDeleteOptionWithConfirmation(index)}
                    onOrderChange={(dragged, newOrder) => handleOptionOrderChange(dragged, newOrder)}
                    onUpdate={newValue => handleOptionUpdate(index, newValue)}
                  />
                  {categoriesErrors[index] && <ValidationMessage>{t(categoriesErrors[index] as string)}</ValidationMessage>}
                </Box>
              ))}
              <AddNewCategoryButton
                variant="outlined"
                onClick={() =>
                  setCustomColumnState({
                    ...customColumnState,
                    fieldOptions: [...(customColumnState.fieldOptions ?? []), { id: generateIdFromString(''), value: '' }],
                  })
                }
              >
                <strong style={{ fontSize: '20px' }}>+</strong>
                {t(translationKeys.CUSTOM_LIST_ADD_NEW_CATEGORY)}
              </AddNewCategoryButton>
            </>
          )}
        </EditCustomColumnSection>
      </PopoverInner>
      {showConfirmDeleteCategoryPopup && (
        <ConfirmPopup
          id="custom-column-confirm-delete-popup"
          width={294}
          isOpen
          title={t(translationKeys.CUSTOM_LIST_CUSTOM_COLUMN_DELETE_CATEGORY)}
          text={t(translationKeys.CUSTOM_LIST_CUSTOM_COLUMN_DELETE_CATEGORY_TEXT)}
          cancelBtnText={t(translationKeys.CANCEL_BUTTON)}
          confirmBtnText={t(translationKeys.DELETE_BUTTON)}
          onCancel={() => setShowConfirmDeleteCategoryPopup(false)}
          onConfirm={() => {
            setShowConfirmDeleteCategoryPopup(false)
            handleOptionDelete(categoryToDelete as number)
          }}
        />
      )}
      {showConfirmUnsavedChangesPopup && (
        <ConfirmPopup
          id="custom-tags-confirm-exit-popup"
          width={294}
          contentWidth={234}
          isOpen
          title={t(translationKeys.UNSAVED_SETTINGS)}
          text={t(translationKeys.CUSTOM_LIST_CUSTOM_COLUMN_YOU_HAVENOT_SAVED_CHANGES)}
          cancelBtnText={t(translationKeys.CUSTOM_LIST_KEEP_EDITING)}
          confirmBtnText={t(translationKeys.CLOSE_ANYWAY)}
          onCancel={() => setShowConfirmUnsavedChangesPopup(false)}
          onConfirm={() => {
            setShowConfirmUnsavedChangesPopup(false)
            onCancel?.()
          }}
        />
      )}
    </CustomPopover>
  )
}

export default memo(EditCustomColumnSectionPopover)
